import { forwardRef, type JSX, type ReactNode, type Ref } from 'react'
import { FormControl, InputLabel, MenuItem, Select, type SelectProps, Typography } from '@mui/material'
import { type Theme, useTheme } from '@mui/material/styles'

import FormErrorHelper from '@Components/utils/errorHelper/FormErrorHelper'
import FormLabel from '@Components/utils/formLabel/FormLabel'
import { getNavigationAndActionIcon } from '@DS/components/icons/navigationAndActionIcons'
import useUuid from '@Hooks/useUuid'

import { type DropdownOption, type DropdownOptionDefaultValueType } from './Dropdown.types'
import * as styles from './styles'

export type DropdownProps<DropdownValueType = DropdownOptionDefaultValueType> = SelectProps<DropdownValueType | ''> & {
  helperText?: ReactNode
  isLabelHidden?: boolean
  options: DropdownOption<DropdownValueType | ''>[]
  required?: boolean
  renderMenuItem?: (option: DropdownOption<DropdownValueType | ''>, theme: Theme) => JSX.Element
}

const ArrowDownIcon = getNavigationAndActionIcon('arrow_down')

function renderMenuItemDefault<DropdownValueType>(
  { label, value, disabled, Icon }: DropdownOption<DropdownValueType>,
  theme: Theme,
) {
  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <MenuItem disableRipple key={label} value={value as any} disabled={disabled}>
      <div css={styles.dropdownMenuItem(theme)}>
        {Icon && <Icon css={styles.dropdownMenuItemIcon(theme)} />}
        <Typography css={styles.dropdownMenuItemText} component="span">
          {label}
        </Typography>
      </div>
    </MenuItem>
  )
}

const Dropdown = forwardRef(
  <DropdownValueType,>(
    {
      label: labelProps,
      id,
      helperText,
      isLabelHidden = false,
      options,
      required = false,
      renderMenuItem = renderMenuItemDefault,
      IconComponent = ArrowDownIcon,
      MenuProps,
      ...rest
    }: DropdownProps<DropdownValueType>,
    ref: Ref<unknown>,
  ): JSX.Element => {
    const theme = useTheme()
    const helperTextId = `${rest.name}-dropdown-text-helper`
    const label = !isLabelHidden && labelProps ? <FormLabel label={labelProps} isRequired={required} /> : undefined
    const inputId = useUuid()
    const labelId = label ? `${id || inputId}-label` : undefined

    return (
      <FormControl css={styles.root(theme, !!rest.value)} fullWidth>
        {!isLabelHidden && (
          <InputLabel disabled={rest.disabled} id={labelId} error={!!helperText}>
            <Typography>{label}</Typography>
          </InputLabel>
        )}
        <Select
          {...rest}
          id={id}
          IconComponent={IconComponent}
          css={styles.select(theme)}
          error={!!helperText}
          labelId={labelId}
          label={label}
          MenuProps={{
            id: `${id || inputId}-options`,
            sx: styles.menu(theme),
            ...MenuProps,
          }}
          inputRef={ref}
        >
          {options.map((option) => renderMenuItem(option, theme))}
        </Select>
        {helperText && (
          <FormErrorHelper id={helperTextId} dataTest={helperTextId} helperText={helperText} error={!!helperText} />
        )}
      </FormControl>
    )
  },
)

export default Dropdown
